import axios from 'axios';
import { crmAttribution } from '../utils/crmTracking';

// API Base URL - Production: https://dscdongle.signulu.com/api
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://mi-dsc-dongle-backend-4384650420.us-central1.run.app/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const isProductAdminRequest = config.url?.includes('/product-admin');
    const token = localStorage.getItem(isProductAdminRequest ? 'productAdminToken' : 'accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (originalRequest?.url?.includes('/product-admin')) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem('productAdminToken');
      }
      return Promise.reject(error);
    }

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          // Use separate axios instance to avoid interceptor loop
          const response = await axios.post(
            `${API_BASE_URL}/auth/refresh`,
            {},
            {
              headers: {
                Authorization: `Bearer ${refreshToken}`,
              },
            }
          );

          const { access_token } = response.data;
          localStorage.setItem('accessToken', access_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================
// AUTH SERVICES
// ============================================
export const authService = {
  // Register new user - sends OTP to email
  register: (data) =>
    api.post('/auth/register', {
      name: data.name,
      email: data.email,
      phone_number: data.phone_number,
      company_name: data.company_name,
      password: data.password,
      confirm_password: data.confirm_password,
      attribution: crmAttribution(),   // where the visitor came from (MITI CRM), or null
    }),

  // Verify email with OTP after registration
  verifyEmail: (data) =>
    api.post('/auth/verify-email', {
      email: data.email,
      otp: data.otp_code,
    }),

  // Login with email and password
  login: (credentials) =>
    api.post('/auth/login', {
      email: credentials.email,
      password: credentials.password,
    }),

  // Request password reset OTP
  forgotPassword: (email) =>
    api.post('/auth/forgot-password', { email }),

  // Verify password reset OTP
  verifyResetOtp: (data) =>
    api.post('/auth/verify-reset-otp', {
      email: data.email,
      otp: data.otp_code,
    }),

  // Reset password with OTP
  resetPassword: (data) =>
    api.post('/auth/reset-password', {
      email: data.email,
      otp: data.otp_code,
      new_password: data.new_password,
      confirm_password: data.confirm_password,
    }),

  // Resend OTP - purpose: 'registration', 'forgot_password', 'email_verification'
  resendOtp: (data) =>
    api.post('/auth/resend-otp', {
      email: data.email,
      purpose: data.purpose || 'registration',
    }),

  // Refresh access token
  refreshToken: () => {
    const refreshToken = localStorage.getItem('refreshToken');
    return axios.post(
      `${API_BASE_URL}/auth/refresh`,
      {},
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    );
  },

  // Get current logged in user
  getCurrentUser: () => api.get('/auth/me'),
};

// ============================================
// PROFILE SERVICES
// ============================================
export const profileService = {
  // Get user profile with subscription details
  getProfile: () => api.get('/profile/'),

  // Update user profile
  updateProfile: (data) =>
    api.put('/profile/', {
      name: data.name,
      phone_number: data.phone_number,
      company_name: data.company_name,
      auto_reminder: data.auto_reminder,
    }),

  // Change password
  changePassword: (data) =>
    api.post('/profile/change-password', {
      current_password: data.current_password,
      new_password: data.new_password,
      confirm_password: data.confirm_password,
    }),

  // Toggle auto reminder
  toggleAutoReminder: (enable) =>
    api.put('/profile/auto-reminder', { enable }),

  // Get reminder status
  getReminderStatus: () => api.get('/profile/reminder-status'),

  // Get subscription history
  getSubscriptionHistory: () => api.get('/profile/subscription-history'),

  // Get access token for EXE application
  getAccessToken: () => api.get('/profile/access-token'),

  // Get expiring subscriptions
  getExpiringSubscriptions: (days = 5) =>
    api.get('/profile/expiring-subscriptions', { params: { days } }),
};

// ============================================
// PAYMENT SERVICES
// ============================================
export const paymentService = {
  // Get all pricing plans
  getPlans: () => api.get('/payment/plans'),

  // Get specific plan by ID
  getPlanById: (id) => api.get(`/payment/plans/${id}`),

  // Start the free trial (1 licence) - NOTE: endpoint path assumed, confirm with backend
  startTrial: () => api.post('/payment/start-trial'),

  // Calculate order amount
  calculateAmount: (data) =>
    api.post('/payment/calculate', {
      plan_id: data.plan_id,
      number_of_dongles: data.number_of_dongles,
    }),

  // Create Razorpay order
  createOrder: (data) =>
    api.post('/payment/create-order', {
      plan_id: data.plan_id,
      number_of_dongles: data.number_of_dongles,
      gstin: data.gstin,
      pan: data.pan,
    }),

  // Verify Razorpay payment
  verifyPayment: (data) =>
    api.post('/payment/verify', {
      razorpay_order_id: data.razorpay_order_id,
      razorpay_payment_id: data.razorpay_payment_id,
      razorpay_signature: data.razorpay_signature,
    }),

  // Get order history
  getOrders: () => api.get('/payment/orders'),

  // Get specific order by ID or order number
  getOrderById: (id) => api.get(`/payment/orders/${id}`),

  // Download the GST invoice (PDF) of a paid order
  downloadInvoice: (id) =>
    api.get(`/payment/orders/${id}/invoice`, { responseType: 'blob' }),

  // Get transaction history
  getTransactions: () => api.get('/payment/transactions'),

  // Get specific transaction
  getTransactionById: (id) => api.get(`/payment/transactions/${id}`),

  // Get payment history (legacy)
  getPaymentHistory: () => api.get('/payment/history'),

  // Get current active subscription
  getCurrentSubscription: () => api.get('/payment/current-subscription'),
};

// ============================================
// SUBSCRIPTION SERVICES
// ============================================
export const subscriptionService = {
  // Get current subscription
  getCurrent: () => paymentService.getCurrentSubscription(),

  // Get subscription history
  getHistory: () => profileService.getSubscriptionHistory(),

  // Get access token for desktop app
  getAccessToken: () => profileService.getAccessToken(),
};

// ============================================
// REMINDER SERVICES
// ============================================
export const reminderService = {
  // Get reminder status
  getStatus: () => profileService.getReminderStatus(),

  // Toggle auto reminder
  toggle: (enable) => profileService.toggleAutoReminder(enable),

  // Get expiring subscriptions
  getExpiring: (days) => profileService.getExpiringSubscriptions(days),
};

// ============================================
// LICENSE TOKEN SERVICES
// ============================================
export const licenseTokenService = {
  // List tokens + usage summary
  list: () => api.get('/license-tokens'),

  // Generate a new token (one per licence/system); documentLimit is optional
  generate: (name, documentLimit) =>
    api.post('/license-tokens/generate', { name, document_limit: documentLimit }),

  // Move documents between licences: [{ token_id, document_limit }]
  updateAllocations: (allocations) => api.put('/license-tokens/allocations', { allocations }),
};

// Product-admin reporting APIs use a separate token from customer login.
export const productAdminService = {
  login: (credentials) => api.post('/product-admin/login', credentials),
  companies: (params = {}) => api.get('/product-admin/companies', { params }),
  company: (companyId, params = {}) => api.get(`/product-admin/companies/${companyId}`, { params }),
  companyAdmins: (companyId) => api.get(`/product-admin/companies/${companyId}/admins`),
  companyTokens: (companyId, params = {}) => api.get(`/product-admin/companies/${companyId}/tokens`, { params }),
  usage: (params = {}) => api.get('/product-admin/usage', { params }),
  tokenUsage: (tokenId, params = {}) => api.get(`/product-admin/tokens/${tokenId}/usage`, { params }),
  settings: () => api.get('/product-admin/settings'),
  updateSettings: (data) => api.put('/product-admin/settings', data),
  setSubscriptionLimit: (subscriptionId, documentLimit) =>
    api.put(`/product-admin/subscriptions/${subscriptionId}/document-limit`, { document_limit: documentLimit }),
  setSubscriptionUnlimited: (subscriptionId) =>
    api.put(`/product-admin/subscriptions/${subscriptionId}/document-limit`, { unlimited: true }),
  // payload: { extend_years: 1 } or { expiry_date: 'YYYY-MM-DD' }
  setSubscriptionValidity: (subscriptionId, payload) =>
    api.put(`/product-admin/subscriptions/${subscriptionId}/validity`, payload),
  setTokenLimit: (tokenId, documentLimit) =>
    api.put(`/product-admin/tokens/${tokenId}/document-limit`, { document_limit: documentLimit }),
};

// ============================================
// HEALTH CHECK
// ============================================
export const healthService = {
  check: () => axios.get(`${API_BASE_URL.replace('/api', '')}/health`),
  root: () => axios.get(API_BASE_URL.replace('/api', '')),
};

// Legacy exports for backward compatibility
export const planService = {
  getPlans: () => paymentService.getPlans(),
  calculatePrice: (params) => paymentService.calculateAmount(params),
};

export const transactionService = {
  getTransactions: () => paymentService.getTransactions(),
  getTransactionById: (id) => paymentService.getTransactionById(id),
};

export default api;
